import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Drawer,
  Menu,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CakeSearchBar from "./CakeSearchBar";
import FilterSidebar from "./FilterSidebar";
import CakeCard from "./CakeCard";
import SelectedFilters from "./SelectedFilters";
import PaginationControls from "./PaginationControls";
import TuneIcon from "@mui/icons-material/Tune";
import SortIcon from "@mui/icons-material/Sort";

const SpecialtyCakesMobileView = ({
  cakes,
  loading,
  filters,
  setFilters,
  clearFilters,
  removeFilter,
  sortOption,
  setSortOption,
  clearSort,
  searchQuery,
  setSearchQuery,
  fetchCakes,
  currentPage,
  getEventTypeLabel,
  handlePageChange,
  attributeOptions,
  showNavbar,
  applyPendingFilters,
}) => {
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [anchorEl, setAnchorEl] = useState(null);
  const openSortMenu = Boolean(anchorEl);

  const toggleFilterDrawer = (open) => () => {
    setFilterDrawerOpen(open);
  };

  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  const handleSortOption = (option) => {
    setSortOption(option);
    fetchCakes();
    setAnchorEl(null);
  };

  const countActiveFilters = (filters) => {
    let count = 0;
    if (filters.recipient !== "All") count++;
    if (filters.eventType !== "All") count++;
    if (filters.style) count++;
    if (filters.decor.length > 0) count += filters.decor.length;
    return count;
  };

  return (
    <Container disableGutters maxWidth="xl">
      {/* Sticky Top Controls */}
      <Box
        sx={{
          position: "sticky",
          top: showNavbar ? "64px" : "0px", // adjust to your app's mobile navbar height
          backgroundColor: "background.paper",
          zIndex: 1000,
          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
          pb: 1,
        }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <CakeSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={(newQuery) => {
              setSearchQuery(newQuery);
              fetchCakes(newQuery);
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            px: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Button
            onClick={toggleFilterDrawer(true)}
            startIcon={<TuneIcon />}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              fontWeight: 500,
              fontSize: 14,
            }}>
            Filters
          </Button>

          <Button
            onClick={handleSortClick}
            startIcon={<SortIcon />}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              fontWeight: 500,
              fontSize: 14,
            }}>
            Sort
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={openSortMenu}
            onClose={handleSortClose}>
            <MenuItem onClick={() => handleSortOption("popularity")}>
              Most Popular
            </MenuItem>
            <MenuItem onClick={() => handleSortOption("price-asc")}>
              Price: Low to High
            </MenuItem>
            <MenuItem onClick={() => handleSortOption("price-desc")}>
              Price: High to Low
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ px: 2, mt: 1 }}>
          <SelectedFilters
            filters={filters}
            sortOption={sortOption}
            removeFilter={removeFilter}
            clearSort={clearSort}
            clearFilters={clearFilters}
            getEventTypeLabel={getEventTypeLabel}
            decorOptions={attributeOptions.decor}
          />
        </Box>
      </Box>

      {/* Cake Grid */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Grid container spacing={2}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "300px",
                py: 10,
              }}>
              <CircularProgress color="primary" size={50} />
              <Typography variant="h6" sx={{ mt: 2, color: "#515151" }}>
                Loading...
              </Typography>
            </Box>
          ) : cakes.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                width: "100%",
                py: 10,
              }}>
              <Typography variant="h5" fontWeight={600}>
                No cakes found.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, opacity: 0.7 }}>
                Try adjusting your filters or clearing search.
              </Typography>
            </Box>
          ) : (
            cakes.map((cake) => (
              <Grid item xs={12} sm={6} key={cake._id}>
                <CakeCard cake={cake} attributes={attributeOptions} />
              </Grid>
            ))
          )}
        </Grid>
      </Box>

      {/* Pagination */}
      {/* <PaginationControls
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
      /> */}

      {/* Filters Drawer */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={toggleFilterDrawer(false)}
        PaperProps={{ sx: { width: "100%" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #eee",
            }}>
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
            <IconButton onClick={toggleFilterDrawer(false)}>✖️</IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
            <FilterSidebar
              filters={pendingFilters}
              setFilters={setPendingFilters}
              clearFilters={() =>
                setPendingFilters({
                  recipient: "All",
                  eventType: "All",
                  decor: [],
                  style: "",
                })
              }
            />
          </Box>

          <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                applyPendingFilters(pendingFilters);
                toggleFilterDrawer(false)();
              }}>
              Apply Filters ({countActiveFilters(pendingFilters)})
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Container>
  );
};

export default SpecialtyCakesMobileView;
